import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { GlassCard } from "./GlassCard";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  MessageCircle, 
  Search, 
  Send, 
  ArrowLeft, 
  User,
  X,
  Loader2,
  Users,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Search Users Component
const UserSearch = ({ onSelectUser, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchUsers = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(q)}`);
      setResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchUsers]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide">
          Buscar Usuarios
        </h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por ID, nombre o email..."
          data-testid="user-search-input"
          className="pl-11 bg-black/50 border-white/10 focus:border-neon-cyan/50 text-white"
          autoFocus
        />
      </div>

      <div className="max-h-64 overflow-y-auto space-y-2">
        {searching && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-neon-cyan animate-spin" />
          </div>
        )}
        
        {!searching && results.length === 0 && query.length >= 2 && (
          <p className="text-center text-slate-500 py-4 font-inter text-sm">
            No se encontraron usuarios
          </p>
        )}
        
        {results.map((user) => (
          <motion.button
            key={user.user_id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => onSelectUser(user)}
            data-testid={`user-result-${user.user_id}`}
            className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-neon-cyan/10 border border-transparent hover:border-neon-cyan/30 transition-all text-left"
          >
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-neon-purple/20 flex items-center justify-center">
                <User className="w-5 h-5 text-neon-purple" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-rajdhani font-semibold text-white truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">ID: {user.user_id}</p>
            </div>
          </motion.button>
        ))}
      </div>
      
      <p className="text-xs text-slate-500 text-center">
        Escribe al menos 2 caracteres para buscar
      </p>
    </div>
  );
};

// Conversation List Component
const ConversationList = ({ conversations, onSelect, onDelete, selectedId, loading }) => {
  const { user } = useAuth();
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (e, convId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    if (deletingId) return;
    
    setDeletingId(convId);
    try {
      await onDelete(convId);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-500 font-inter text-sm">
          No tienes conversaciones aún
        </p>
        <p className="text-slate-600 font-inter text-xs mt-1">
          Busca usuarios para empezar a chatear
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {conversations.map((conv) => {
        // Get other participant's name
        const otherUserId = conv.participants.find(p => p !== user?.user_id);
        const otherName = conv.participant_names?.[otherUserId] || "Usuario";
        
        return (
          <motion.div
            key={conv.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`group relative flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer ${
              selectedId === conv.id
                ? "bg-neon-cyan/20 border border-neon-cyan/50"
                : "bg-white/5 hover:bg-white/10 border border-transparent"
            }`}
            onClick={() => onSelect(conv)}
            data-testid={`conversation-${conv.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-neon-purple/20 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-neon-purple" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="font-rajdhani font-semibold text-white truncate">{otherName}</p>
                {conv.unread_count > 0 && (
                  <span className="bg-neon-cyan text-black text-xs font-bold px-2 py-0.5 rounded-full">
                    {conv.unread_count}
                  </span>
                )}
              </div>
              {conv.last_message && (
                <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
              )}
            </div>
            
            {/* Delete button */}
            <button
              onClick={(e) => handleDelete(e, conv.id)}
              disabled={deletingId === conv.id}
              data-testid={`delete-conv-${conv.id}`}
              className="opacity-0 group-hover:opacity-100 p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-all"
              title="Eliminar conversación"
            >
              {deletingId === conv.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </motion.div>
        );
      })}
    </div>
  );
};

// Chat Messages Component
const ChatMessages = ({ conversation, onBack }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  // Get other participant's name
  const otherUserId = conversation.participants.find(p => p !== user?.user_id);
  const otherName = conversation.participant_names?.[otherUserId] || "Usuario";

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/conversations/${conversation.id}/messages`
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  }, [conversation.id]);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 3 seconds
    pollInterval.current = setInterval(fetchMessages, 3000);
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/conversations/${conversation.id}/messages`,
        { content: newMessage.trim() }
      );
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-white/10">
        <button 
          onClick={onBack}
          className="text-slate-400 hover:text-white lg:hidden"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full bg-neon-purple/20 flex items-center justify-center">
          <User className="w-5 h-5 text-neon-purple" />
        </div>
        <div>
          <p className="font-rajdhani font-semibold text-white">{otherName}</p>
          <p className="text-xs text-slate-500">ID: {otherUserId}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 font-inter text-sm">
              No hay mensajes aún
            </p>
            <p className="text-slate-600 font-inter text-xs mt-1">
              ¡Envía el primer mensaje!
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === user?.user_id;
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                    isOwn
                      ? "bg-neon-cyan/20 border border-neon-cyan/30 text-white rounded-br-md"
                      : "bg-white/10 border border-white/10 text-slate-200 rounded-bl-md"
                  }`}
                >
                  <p className="font-inter text-sm break-words">{msg.content}</p>
                  <p className={`text-xs mt-1 ${isOwn ? "text-neon-cyan/60" : "text-slate-500"}`}>
                    {new Date(msg.timestamp).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Escribe un mensaje..."
            data-testid="chat-message-input"
            className="flex-1 bg-black/50 border-white/10 focus:border-neon-cyan/50 text-white"
            disabled={sending}
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            data-testid="send-message-btn"
            className="px-4 bg-neon-cyan/20 hover:bg-neon-cyan/30 text-neon-cyan border border-neon-cyan/50"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Main Chat Component
export const ChatPage = () => {
  const { user, authMode } = useAuth();
  const [view, setView] = useState("list"); // "list" | "search" | "chat"
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authMode !== "incognito") {
      fetchConversations();
      // Poll for new conversations
      const interval = setInterval(fetchConversations, 5000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [authMode, fetchConversations]);

  const handleSelectUser = async (selectedUser) => {
    try {
      const response = await axios.post(
        `${API}/conversations/${selectedUser.user_id}`,
        {}
      );
      setSelectedConversation(response.data);
      setView("chat");
      fetchConversations();
    } catch (error) {
      toast.error("Error al iniciar conversación");
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setView("chat");
  };

  const handleDeleteConversation = async (convId) => {
    try {
      await axios.delete(`${API}/conversations/${convId}`);
      toast.success("Conversación eliminada");
      
      // If we're viewing the deleted conversation, go back to list
      if (selectedConversation?.id === convId) {
        setSelectedConversation(null);
        setView("list");
      }
      
      // Refresh conversations list
      fetchConversations();
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error("Error al eliminar conversación");
    }
  };

  // Incognito users can't use chat
  if (authMode === "incognito") {
    return (
      <GlassCard className="p-8 text-center">
        <MessageCircle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h2 className="heading-cyber text-2xl mb-2">Chat no disponible</h2>
        <p className="text-slate-400 font-inter">
          El chat solo está disponible para usuarios con cuenta MoodPunk o Google.
        </p>
        <p className="text-slate-500 font-inter text-sm mt-2">
          Crea una cuenta para conectar con otros usuarios.
        </p>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-0 overflow-hidden" style={{ height: "calc(100vh - 180px)", minHeight: "500px" }}>
      <div className="flex h-full">
        {/* Sidebar - Conversations */}
        <div className={`w-full lg:w-80 border-r border-white/10 flex flex-col ${
          view === "chat" ? "hidden lg:flex" : "flex"
        }`}>
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-cyber text-xl">Chat</h2>
              <Button
                onClick={() => setView(view === "search" ? "list" : "search")}
                data-testid="search-users-btn"
                className="p-2 bg-neon-cyan/10 hover:bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30 rounded-lg"
              >
                {view === "search" ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
              </Button>
            </div>
            
            {/* User ID display */}
            <div className="bg-black/30 rounded-lg p-2 text-center">
              <p className="text-xs text-slate-500 font-rajdhani">Tu ID:</p>
              <p className="text-sm text-neon-cyan font-mono truncate" data-testid="my-user-id">
                {user?.user_id}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <AnimatePresence mode="wait">
              {view === "search" ? (
                <motion.div
                  key="search"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <UserSearch 
                    onSelectUser={handleSelectUser} 
                    onClose={() => setView("list")}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <ConversationList
                    conversations={conversations}
                    onSelect={handleSelectConversation}
                    onDelete={handleDeleteConversation}
                    selectedId={selectedConversation?.id}
                    loading={loading}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 ${view !== "chat" ? "hidden lg:flex" : "flex"} flex-col`}>
          {selectedConversation ? (
            <ChatMessages
              conversation={selectedConversation}
              onBack={() => {
                setView("list");
                fetchConversations();
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 font-inter">
                  Selecciona una conversación o busca usuarios
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
};
